"use client";

import React, { useEffect, useContext, useCallback } from "react";
import { UserContext } from "@/context/ContextProvider";
import Navbar from "@/components/Navbar/Navbar";
import Footer from "@/components/Footer/Footer";
import SidebarLeft from "@/components/Navbar/SidebarLeft";
import SidebarRight from "@/components/Navbar/SidebarRight";
import { usePathname } from "next/navigation";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { useRouter } from "next/navigation";

import "./globals.css";
import { CookiesModal } from "@/components/Cookies/CookiesModal";
import {
  auth,
  signInWithEmailLink,
  isSignInWithEmailLink,
} from "@/config/firebaseConfig";

export default function AppLayout({
  children,
  setIsSidebarRightOpen,
  setIsSidebarLeftOpen,
  isSidebarRightOpen,
  isSidebarLeftOpen,
}) {
  const { isCookiePopupOpen, setIsCookiePopupOpen } = useContext(UserContext);

  const router = useRouter();

  useEffect(() => {
    const handleSignIn = async () => {
      if (isSignInWithEmailLink(auth, window.location.href)) {
        let email = window.localStorage.getItem("emailForSignIn");

        if (!email) {
          const urlParams = new URLSearchParams(window.location.search);
          email = urlParams.get("email");
          email = email ? decodeURIComponent(email) : null;
        }

        // 3️⃣ NEW: decode the *entire* search string & reparse
        if (!email) {
          const raw = window.location.search;
          const decoded = decodeURIComponent(raw);
          const nested = new URLSearchParams(decoded);
          email = nested.get("email") ?? undefined;
        }

        if (!email) {
          console.error("E-Mail-Adresse nicht gefunden.");
          alert(
            "Ihr Link ist ungültig oder abgelaufen. Bitte fordern Sie einen neuen Link an."
          );
          return;
        }

        try {
          await signInWithEmailLink(auth, email, window.location.href);
          window.localStorage.removeItem("emailForSignIn");
        } catch (error) {
          if (error.code === "auth/invalid-action-code") {
            router.push("/link-abgelaufen");
          } else if (error.code === "auth/expired-action-code") {
            router.push("/link-abgelaufen");
          } else {
            router.push("/link-abgelaufen");
          }
        }
      }
    };

    handleSignIn();
  }, []);

  const handleOutsideClick = useCallback(
    (event) => {
      if (
        isSidebarRightOpen &&
        !event.target.closest(".sidebarRight") &&
        !event.target.closest(".navbar-right-toggle")
      ) {
        setIsSidebarRightOpen(false);
      }
      if (
        isSidebarLeftOpen &&
        !event.target.closest(".sidebarLeft") &&
        !event.target.closest(".navbar-left-toggle")
      ) {
        setIsSidebarLeftOpen(false);
      }
    },
    [isSidebarRightOpen, isSidebarLeftOpen] // ✅ Dependencies to track changes
  );

  useEffect(() => {
    document.addEventListener("click", handleOutsideClick);

    return () => {
      document.removeEventListener("click", handleOutsideClick);
    };
  }, [handleOutsideClick]); // ✅ Now it doesn't warn about missing dependencies

  // Extract current path
  const pathnameurl = usePathname();

  const pathsToHideFooter = [
    /^\/[^/]+\/profil$/,
    /^\/[^/]+\/profil\/details$/,
    /^\/[^/]+\/profil\/fotos$/,
    /^\/[^/]+\/profil\/fahrzeug-verkauft$/,
    /^\/[^/]+\/profil\/fotos\/fotos-schiessen$/,
    /^\/[^/]+\/profil\/fotos\/fotos-schiessen\/exterior$/,
    /^\/[^/]+\/profil\/fotos\/fotos-schiessen\/interior$/,
    /^\/[^/]+\/profil\/fotos\/fotos-schiessen\/felgen$/,
    /^\/[^/]+\/profil\/fotos\/fotos-schiessen\/reifen$/,
    /^\/[^/]+\/profil\/fotos\/fotos-schiessen\/schaeden$/,
    /^\/[^/]+\/profil\/fotos\/fotos-schiessen\/schaeden\/felgenschaeden$/,
    /^\/[^/]+\/profil\/fotos\/fotos-schiessen\/schaeden\/reifen$/,
    /^\/[^/]+\/profil\/fotos\/fotos-schiessen\/schaeden\/windschutzscheibe$/,
    /^\/[^/]+\/profil\/fotos\/fotos-schiessen\/schaeden\/anbauteile$/,
    /^\/[^/]+\/profil\/fotos\/fotos-schiessen\/schaeden\/kratzer-oder-schrammen$/,
    /^\/[^/]+\/profil\/fotos\/fotos-schiessen\/schaeden\/dellen$/,
    /^\/[^/]+\/profil\/fotos\/fotos-schiessen\/schaeden\/mechanische-oder-elektrische$/,
    /^\/[^/]+\/profil\/fotos\/fotos-schiessen\/schaeden\/lackprobleme$/,
    /^\/[^/]+\/profil\/fotos\/fotos-schiessen\/schaeden\/warnleuchten$/,
    /^\/[^/]+\/profil\/fotos\/fotos-schiessen\/schaeden\/zusatz$/,
    /^\/[^/]+\/profil\/service-historie$/,
    /^\/[^/]+\/profil\/service-historie\/fotos$/,
    /^\/[^/]+\/profil\/merkmale-und-ausstattung\/sonderausstatung$/,
    /^\/[^/]+\/profil\/merkmale-und-ausstattung\/sitzmaterial$/,
    /^\/[^/]+\/profil\/merkmale-und-ausstattung\/auto-schluessel$/,
    /^\/[^/]+\/profil\/merkmale-und-ausstattung\/pickerl$/,
    /^\/[^/]+\/profil\/merkmale-und-ausstattung\/diebstahlsicherungsmutter$/,
    /^\/[^/]+\/profil\/merkmale-und-ausstattung\/rauchen$/,
    /^\/[^/]+\/profil\/merkmale-und-ausstattung\/vorbesitzer$/,
    /^\/[^/]+\/profil\/fahrzeug-eigentum\/zulassungsbescheinigung-teil-eins$/,
    /^\/[^/]+\/profil\/fahrzeug-eigentum\/fahrzeug-halter$/,
    /^\/[^/]+\/profil\/fahrzeug-eigentum\/fahrzeug-halter\/jemand-anders$/,
    /^\/[^/]+\/profil\/fahrzeug-eigentum\/fahrzeug-halter\/unternehmen$/,
    /^\/[^/]+\/profil\/fahrzeug-eigentum\/finanzierung$/,
    /^\/[^/]+\/profil\/fahrzeug-eigentum\/versicherung$/,
    /^\/[^/]+\/profil\/fahrzeug-eigentum\/standort$/,
    /^\/[^/]+\/profil\/zusammenfassung$/,
  ];

  // Check if the current path matches any pattern in the array
  const hideFooter = pathsToHideFooter.some((pattern) =>
    pattern.test(pathnameurl)
  );

  return (
    <>
      <ToastContainer
        position="top-right"
        autoClose={5000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick={false}
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        theme="light"
      />
      <CookiesModal
        isOpen={isCookiePopupOpen}
        setIsOpen={setIsCookiePopupOpen}
        disableClose={true}
      />

      <Navbar
        setIsSidebarRightOpen={setIsSidebarRightOpen}
        isSidebarRightOpen={isSidebarRightOpen}
        setIsSidebarLeftOpen={setIsSidebarLeftOpen}
      />

      <SidebarRight
        setIsSidebarRightOpen={setIsSidebarRightOpen}
        isSidebarRightOpen={isSidebarRightOpen}
      />

      <SidebarLeft
        setIsSidebarLeftOpen={setIsSidebarLeftOpen}
        isSidebarLeftOpen={isSidebarLeftOpen}
      />

      <main>{children}</main>
      {/* Conditionally render the footer */}
      {!hideFooter && <Footer />}
    </>
  );
}
